import React, { useEffect, useMemo, useState } from 'react';
import { Command } from 'cmdk';
import { useQuery } from '@tanstack/react-query';
import { fetchOnus } from '../lib/api.js';
import { parseName } from '../lib/format.js';

const LIMIT = 12;
const DEBOUNCE_MS = 200;

function useDebounced(value, ms) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function CommandPalette({ open, onClose, onSelectOnu }) {
  const [input, setInput] = useState('');
  const search = useDebounced(input.trim(), DEBOUNCE_MS);

  // Limpa o input quando o palette fecha — abre sempre vazio.
  useEffect(() => {
    if (!open) setInput('');
  }, [open]);

  const enabled = open && search.length >= 2;

  const { data, isFetching, error } = useQuery({
    queryKey: ['palette', 'onus', search],
    queryFn: () => fetchOnus({ status: 'all', search, limit: LIMIT }),
    enabled,
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  });

  const items = useMemo(() => {
    const env = data?.data;
    if (Array.isArray(env)) return env;
    return env?.items || [];
  }, [data]);

  function handleSelect(onu) {
    onSelectOnu(onu);
    onClose();
  }

  return (
    <Command.Dialog
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      label="Busca global de ONUs"
      shouldFilter={false}
      overlayClassName="techdash-cmdk-overlay"
      contentClassName="techdash-cmdk-content"
    >
      <PaletteStyles />
      <div style={inputWrap}>
        <Command.Input
          autoFocus
          placeholder="Cliente, contrato, SN, CEP, OLT…"
          value={input}
          onValueChange={setInput}
          style={inputStyle}
        />
        {isFetching && enabled ? <span style={hintStyle}>buscando…</span> : null}
      </div>

      <Command.List style={listStyle}>
        {error ? (
          <div style={emptyStyle}>Erro: {String(error.message || error)}</div>
        ) : null}

        {!enabled ? (
          <div style={emptyStyle}>
            Digite ao menos 2 caracteres para buscar.
          </div>
        ) : items.length === 0 && !isFetching ? (
          <Command.Empty style={emptyStyle}>
            Nada encontrado para “{search}”.
          </Command.Empty>
        ) : null}

        {items.map((onu) => {
          const { display, subtitle } = parseName(onu.person_name);
          const line2 = [
            onu.contract_code || onu.contract_id,
            onu.olt_code || onu.olt_name,
            onu.postal_code,
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <Command.Item
              key={onu.onu_sn}
              value={onu.onu_sn}
              onSelect={() => handleSelect(onu)}
              style={itemStyle}
            >
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <div style={primaryLine}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {display || onu.person_name || onu.onu_sn}
                  </span>
                  {subtitle ? <span style={partnerTag}>{subtitle}</span> : null}
                </div>
                {line2 ? <div style={secondaryLine}>{line2}</div> : null}
              </div>
              <code style={snStyle}>{onu.onu_sn}</code>
            </Command.Item>
          );
        })}
      </Command.List>

      <div style={footerStyle}>
        <span><kbd style={kbdStyle}>↑↓</kbd> navegar</span>
        <span><kbd style={kbdStyle}>↵</kbd> abrir</span>
        <span><kbd style={kbdStyle}>esc</kbd> fechar</span>
      </div>
    </Command.Dialog>
  );
}

// Estilos inline encapsulados (sem CSS module/global para não inflar bundle).
const inputWrap = {
  display: 'flex',
  alignItems: 'center',
  padding: '14px 18px',
  borderBottom: '1px solid #e5e7eb',
};
const inputStyle = {
  flex: 1,
  border: 'none',
  outline: 'none',
  fontSize: 16,
  background: 'transparent',
  color: '#111827',
};
const hintStyle = { fontSize: 12, color: '#9ca3af', marginLeft: 8 };
const listStyle = {
  maxHeight: 360,
  overflowY: 'auto',
  padding: '6px 0',
};
const emptyStyle = {
  padding: '24px 18px',
  fontSize: 13,
  color: '#6b7280',
  textAlign: 'center',
};
const itemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 18px',
  cursor: 'pointer',
  fontSize: 13,
};
const primaryLine = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  color: '#111827',
  fontWeight: 500,
  minWidth: 0,
};
const partnerTag = {
  fontSize: 11,
  color: '#6b7280',
  background: '#f3f4f6',
  padding: '1px 6px',
  borderRadius: 4,
  flexShrink: 0,
};
const secondaryLine = {
  fontSize: 12,
  color: '#6b7280',
  marginTop: 2,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const snStyle = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 11,
  color: '#9ca3af',
  flexShrink: 0,
};
const footerStyle = {
  display: 'flex',
  gap: 16,
  padding: '8px 18px',
  borderTop: '1px solid #e5e7eb',
  fontSize: 11,
  color: '#9ca3af',
  background: '#f9fafb',
};
const kbdStyle = {
  fontFamily: 'ui-monospace, monospace',
  background: '#fff',
  border: '1px solid #d1d5db',
  borderRadius: 3,
  padding: '0 5px',
  marginRight: 4,
  fontSize: 11,
};

// Styles globais do dialog (overlay + posicionamento) — injetados uma vez via
// <style>. Evita criar novo CSS file e dar conflito com react-scripts.
function PaletteStyles() {
  return (
    <style>{`
      .techdash-cmdk-overlay {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.7);
        z-index: 1000;
        animation: techdash-cmdk-fade 120ms ease-out;
      }
      .techdash-cmdk-content {
        position: fixed;
        top: 12vh;
        left: 50%;
        transform: translateX(-50%);
        width: min(600px, calc(100vw - 32px));
        background: #fff;
        border-radius: 12px;
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        z-index: 1001;
        overflow: hidden;
        animation: techdash-cmdk-pop 120ms ease-out;
      }
      .techdash-cmdk-content [cmdk-item][data-selected="true"] {
        background: #eef2ff;
        color: #1e40af;
      }
      @keyframes techdash-cmdk-fade {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes techdash-cmdk-pop {
        from { opacity: 0; transform: translate(-50%, -8px); }
        to { opacity: 1; transform: translate(-50%, 0); }
      }
    `}</style>
  );
}
